import { action, computed, flow, makeObservable, observable } from 'mobx';
import {
  GENERIC_ERROR_MESSAGE,
  isEmptyObject,
  isNumber,
} from '@roc/feature-utils';
import { GlobalStore } from '@roc/feature-app-core';
import {
  BorrowerEntityInformationSteps,
  SelectEntityOption,
} from '../../utils/constants';
import { BorrowerEntityFormStore } from '../borrowerEntityFormStore';
import { BaseBorrowerInformationStore } from '../baseBorrowerInformationStore';
import { NewLoanApplicationService } from 'libs/feature-borrower-loan-submission/src/modules/newLoanApplication/services/newLoanApplicationService';
import {
  getEntityInformationSteps as _getEntityInformationSteps,
  mapEntityResponse,
} from '../../utils/borrowerLoanSubmissionUtils';
import { SearchEntityGridStore } from '../entity/searchEntityGridStore';
import { GENERIC_VALIDATION_NOTIFICATION } from '../../../../../feature-app-core/src/stores/formStore';
import { SelectEntityOption as contants } from '../../utils/constants';
import { SinglePropertyQuoteBorrowerEntityFormStore } from './singlePropertyQuoteBorrowerEntityFormStore';
import { BorrowerEntityInformationStore } from '../borrowerEntityInformationStore';

export class SinglePropertyQuoteBorrowerEntityInformationStore extends BorrowerEntityInformationStore {
  public selectEntityOption: SelectEntityOption;
  public searchEntityGridStore: SearchEntityGridStore;

  constructor(
    globalStore: GlobalStore,
    borrowerInformationStore: BaseBorrowerInformationStore
  ) {
    super(globalStore, borrowerInformationStore);
    const entityFormStore = new SinglePropertyQuoteBorrowerEntityFormStore(
      globalStore
    ) as unknown;
    this.borrowerEntityFormStore = entityFormStore as BorrowerEntityFormStore;
    this.searchEntityGridStore = new SearchEntityGridStore(globalStore, this);

    makeObservable(this, {
      goToFirstSubstep: flow,
      setSelectEntityOption: action,
      clearSelectedEntity: action,
      getEntityInformationSteps: action,
      validateCurrentStep: action,
      goToPreviousSubstep: action,
      goToNextSubstep: action,
      allowNext: computed,
    });
  }

  setSelectEntityOption(option) {
    if (option === contants.NO_ENTITY) {
      this.mainEntity = {};
      this.borrowerEntityFormStore.reset();
    }
    this.selectEntityOption = option;
  }

  clearSelectedEntity() {
    this.borrowerInformationStore.reset();
    this.borrowerEntityFormStore.reset();
  }

  *goToFirstSubstep() {
    if (
      (this.borrowerEntityInformationActiveStep =
        BorrowerEntityInformationSteps.ENTITY_INFORMATION)
    ) {
      this.borrowerEntityInformationActiveStep = this.getEntityInformationSteps()[0];
    }
  }

  getEntityInformationSteps() {
    const borrower = this.borrowerInformationStore.mainBorrower;
    return _getEntityInformationSteps({
      isCitizenshipEmpty: !borrower.citizenshipStatus,
      isFicoOrExperienceEmpty:
        !borrower.rentalExperience || !borrower.ficoProvidedAtOrigination,
      selectEntityOption: this.selectEntityOption,
    });
  }

  goToPreviousSubstep() {
    const steps = this.getEntityInformationSteps();
    const currentIndex = steps.indexOf(
      this.borrowerEntityInformationActiveStep
    );
    this.borrowerEntityInformationActiveStep = steps[currentIndex - 1];
  }

  goToNextSubstep() {
    const steps = this.getEntityInformationSteps();
    if (this.validateCurrentStep()) {
      const currentIndex = steps.indexOf(
        this.borrowerEntityInformationActiveStep
      );
      this.borrowerEntityInformationActiveStep = steps[currentIndex + 1];
    }
  }

  validateCurrentStep() {
    const step = this.borrowerEntityInformationActiveStep;
    const {
      ficoProvidedAtOrigination,
      rentalExperience,
    } = this.borrowerInformationStore.mainBorrowerFormStore.form.fields;

    switch (step) {
      case BorrowerEntityInformationSteps.BORROWER_CREDIT:
        if (ficoProvidedAtOrigination.value < 660) {
          this.globalStore.notificationStore.showWarningNotification({
            message:
              'Sorry we cannot underwrite loans where the borrowers have less than 660 mid-score FICO',
          });
          return false;
        }
        if (ficoProvidedAtOrigination.value > 850) {
          this.globalStore.notificationStore.showWarningNotification({
            message:
              'Sorry we cannot underwrite loans where the borrowers have more than 850 mid-score FICO',
          });
          return false;
        }
        if (typeof rentalExperience.value !== 'number') {
          rentalExperience.error = 'This field is required';
          this.globalStore.notificationStore.showWarningNotification({
            message: GENERIC_VALIDATION_NOTIFICATION,
          });
          return false;
        }
        return true;
      case BorrowerEntityInformationSteps.ADD_ENTITY:
        this.borrowerEntityFormStore.runFormValidationWithMessage();
        this.mainEntity = this.borrowerEntityFormStore.getFormValues();
        return this.borrowerEntityFormStore.form.meta.isValid;
      case BorrowerEntityInformationSteps.SEARCH_ENTITY:
        if (!this.borrowerEntityFormStore.form.fields.name.value) {
          this.globalStore.notificationStore.showWarningNotification({
            message: 'Please select an entity to continue.',
          });
          return false;
        }
        return true;
      case BorrowerEntityInformationSteps.BORROWER_INFORMATION:
        const areAllBorrowersValid = this.borrowerInformationStore.coBorrowersFormStores.every(
          store => {
            store.runFormValidationWithMessage();
            return store.form.meta.isValid;
          }
        );
        return areAllBorrowersValid;
      default:
        return true;
    }
  }

  get allowNext() {
    const step = this.borrowerEntityInformationActiveStep;
    return (
      step !== BorrowerEntityInformationSteps.BORROWER_CITIZENSHIP &&
      step !== BorrowerEntityInformationSteps.SELECT_ENTITY_OPTION
    );
  }
}
